import { Text, Stack, Heading, Box } from "@chakra-ui/react";
import { ReactNode } from "react";
import { motion } from "framer-motion";
import NextImage from "next/image";

import CustomWrapper from "components/Chakra/CustomWrapper";
import IconDog from "components/Chakra/Icons/Dog";
import IconHeart from "components/Chakra/Icons/Heart";
import CustomLink from "components/Chakra/CustomLink";
import { TAppRoutes } from "hooks/useRouter";
import { useLoginModal } from "modals/useLoginModal";
import IconBriefcase from "components/Chakra/Icons/Briefcase";
import IconKey from "components/Chakra/Icons/Key";
import IconPolicy from "components/Chakra/Icons/Policy";
import IconINCircle from "components/Chakra/Icons/INCircle";
import IconMoney from "components/Chakra/Icons/Money";
import IconSelfCheck from "components/Chakra/Icons/SelfCheck";
import IconDocument from "components/Chakra/Icons/Document";
import IconCam from "components/Chakra/Icons/Cam";
import IconHouse from "components/Chakra/Icons/House";
import IconPerson from "components/Chakra/Icons/Person";
import IconArrowRight from "components/Chakra/Icons/ArrowRight";
import IconCrown from "components/Chakra/Icons/Crown";
import { TrackEventProps } from "contexts/Tracking";

type Services = {
  title: string;
  icon: ReactNode;
  to?: TAppRoutes;
  href?: string;
  type: "MODAL" | "LINK";
  isDisabled?: boolean;
  target?: string;
  trackEventPayload?: TrackEventProps;
};

const items: Array<Services> = [
  {
    title: "Inzerce pro realitní kanceláře",
    icon: <IconBriefcase fill="ud-primary" />,
    to: "/pro-realitni-kancelare",
    type: "LINK",
  },
  {
    title: "Inzerce pro soukromé majitele",
    icon: <IconKey fill="ud-primary" />,
    to: "/vyhody/soukroma-inzerce",
    type: "LINK",
  },
  {
    title: "Realitní blog",
    icon: <IconHeart fill="ud-primary" />,
    to: "/radce",
    type: "LINK",
  },
  {
    title: "Hlídací pes",
    icon: <IconDog fill="ud-primary" />,
    to: "/hlidaci-pes",
    type: "LINK",
  },
  {
    title: "Prověření rizikovosti osob",
    icon: <IconPolicy fill="ud-primary" />,
    to: "/provereni-rizikovosti-osob",
    type: "LINK",
  },
  {
    title: "Služba Ideální nájemce",
    icon: <IconINCircle fill="ud-primary" />,
    href: "https://idealninajemce.cz/",
    type: "LINK",
  },
  {
    title: "Nacenění nemovitosti",
    icon: <IconMoney fill="ud-primary" />,
    href: "https://idealninajemce.cz/pronajimam/naceneni-najmu",
    type: "LINK",
  },
  {
    title: "Sebeprověření",
    icon: <IconSelfCheck fill="ud-primary" />,
    to: "/sebeprovereni",
    type: "LINK",
  },
  {
    title: "Balíčky Prověření osob",
    icon: <IconPolicy fill="ud-primary" />,
    to: "/balicky-provereni",
    type: "LINK",
  },
  {
    title: "Vzory smluv",
    icon: <IconDocument fill="ud-primary" />,
    href: "https://idealninajemce.cz/pronajimam/vzory-smluv",
    type: "LINK",
  },
  {
    title: "Cenové mapy",
    icon: <IconMoney fill="ud-primary" />,
    href: "https://idealninajemce.cz/cenova-mapa-najemneho",
    type: "LINK",
  },
  {
    title: "Průvodce nájemníka",
    icon: <IconDocument fill="ud-primary" />,
    to: "/pruvodce-najemnika",
    type: "LINK",
  },
  {
    title: "Virtuální prezentace",
    icon: <IconCam fill="ud-primary" />,
    type: "LINK",
    to: "/vizualni-prezentace",
  },
  {
    title: "Prodej nemovitostí",
    icon: <IconHouse fill="ud-primary" />,
    href: "https://prodeje.ulovdomov.cz/",
    type: "LINK",
  },
  {
    title: "Obsazení bez starostí",
    icon: <IconCrown fill="ud-primary" />,
    href: "https://idealninajemce.cz/pronajimam/obsazeni-bez-starosti",
    type: "LINK",
  },
  {
    title: "Profil bydlení",
    icon: <IconPerson fill="ud-primary" />,
    type: "MODAL",
  },
  {
    title: "Aplikace Houmy",
    icon: (
      <NextImage
        src="/img/icons/mobile-phone.svg"
        alt=""
        width="12"
        height="16"
      />
    ),
    href: "https://www.houmy.cz/",
    type: "LINK",
    target: "_blank",
    trackEventPayload: {
      "dl.eCat": "homepage",
      "dl.eAct": "bttn.clicked",
      "dl.eLab": "Aplikace Houmy",
    },
  },
];

const Services = () => {
  const { openModal: openLoginModal } = useLoginModal();

  return (
    <CustomWrapper
      maxW="1360px"
      mt={{ base: "40px", md: "80px" }}
      position="relative"
    >
      <Heading textAlign="center" mb="40px">
        Pomáháme spokojeně bydlet, pronajímat i prodat
      </Heading>
      <Stack
        direction={{ base: "column", md: "row" }}
        wrap="wrap"
        spacing={0}
        gap="16px"
        margin="0 auto"
        justify={{ base: "center", xl: "start" }}
      >
        {items.map(
          (
            {
              title,
              icon,
              href,
              to,
              type,
              target,
              trackEventPayload,
              isDisabled = false,
            },
            index,
          ) => (
            <CustomLink
              as={type === "LINK" ? "a" : "button"}
              to={to}
              href={href}
              key={index}
              cursor={isDisabled ? "not-allowed" : "pointer"}
              w={{ base: "100%", md: "310px", xl: "calc(25% - 12px)" }}
              gridGap={2}
              target={target}
              trackEventPayload={trackEventPayload}
              onClick={() => {
                if (type === "MODAL") {
                  openLoginModal({});
                }
              }}
              _hover={{
                svg: {
                  opacity: 1,
                },
              }}
            >
              <motion.div
                initial={{
                  backgroundColor: "inherit",
                  borderColor: "#CCCCCC",
                }}
                whileHover={{
                  backgroundColor: "#F7F7F7",
                  borderColor: "transparent",
                  transition: { duration: 0.3 },
                }}
                animate={{
                  backgroundColor: "inherit",
                  borderColor: "#CCCCCC",
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  position: "relative",
                  width: "100%",
                  flexDirection: "row",
                  padding: "16px",
                  gap: "6px",
                  borderWidth: "1px",
                  borderRadius: "8px",
                }}
              >
                <Box width="16px">{icon}</Box>
                <Text textAlign="center">{title}</Text>
                <IconArrowRight
                  boxSize="20px"
                  fill="ud-primary"
                  position="absolute"
                  right="16px"
                  opacity={0}
                  transition="opacity 0.3s ease"
                />
              </motion.div>
            </CustomLink>
          ),
        )}
      </Stack>
    </CustomWrapper>
  );
};

export default Services;
